/**
 * Canonical entity identity + derived joins for USER-SUPPLIED data on the
 * fusion canvas.
 *
 * Problem this solves: paste cards, image-extracted artefacts and bank-import
 * counterparties each minted their own random node ids
 * (`intake:vpa:1719…`, `user:vpa:1719…`, `sf:cp:3:…`), so the same real-world
 * identifier entered twice became two disconnected nodes and the officer's
 * own data never cross-linked. Canonical ids make identity a function of the
 * VALUE: every producer that adds "9876543210@ybl" lands on the same
 * `ent:vpa:9876543210@ybl` node, and edges from different surfaces meet there.
 *
 * Derived joins add the one inference we can make honestly client-side: a VPA
 * whose local part is a 10-digit number IS keyed on that phone number, so if
 * that phone node exists we draw a SCORED (dashed, court-safe-dimmed)
 * `shared identifier` edge — a lead, not a proven fact.
 */
import type cytoscape from 'cytoscape';

export function normalizeIdentifier(type: string, value: string): string {
  const v = (value || '').trim();
  switch (type) {
    case 'phone': {
      // Indian-first: strip separators, then the +91 / leading-0 prefixes so
      // "+91 98765 43210", "098765 43210" and "9876543210" are one identity.
      let d = v.replace(/\D/g, '');
      if (d.length > 10 && d.startsWith('91')) d = d.slice(-10);
      else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
      return d;
    }
    case 'vpa':
      return v.toLowerCase();
    case 'handle':
      return v.replace(/^@/, '').toLowerCase();
    case 'wallet': {
      // Strip canvas / chain-prefixed ids; R1 casing: EVM → lowercase, TRX/BTC/SOL base58 preserved.
      return bareWalletAddress(v);
    }
    default:
      return v;
  }
}

/** Strip Fusion ``ent:wallet:`` and ``TRX:``/``ETH:`` style prefixes so Workbench
 *  APIs receive a bare chain address. */
export function bareWalletAddress(value: string): string {
  let w = (value || '').trim().replace(/^ent:wallet:/i, '');
  const m = /^(TRX|ETH|BTC|SOL|BSC|BNB|MATIC|POLYGON|ARB|BASE|OP|AVAX|TON|XRP|DOGE|LTC|XMR|ZEC):(.+)$/i.exec(w);
  if (m) w = m[2].trim();
  return w.startsWith('0x') ? w.toLowerCase() : w;
}

/** Deterministic node id for a user-supplied identifier — same value ⇒ same node. */
export const canonicalEntityId = (type: string, value: string): string =>
  `ent:${type}:${normalizeIdentifier(type, value)}`;

export const looksLikeVpa = (v: string): boolean =>
  /^[a-z0-9][a-z0-9._-]*@[a-z][a-z0-9]{1,}$/i.test((v || '').trim());

export const looksLikePhone = (v: string): boolean => {
  const t = (v || '').trim();
  if (!/^[+\d][\d\s()-]*$/.test(t)) return false;
  const d = t.replace(/\D/g, '');
  return d.length >= 10 && d.length <= 12;
};

// ── Subject identity hub ──────────────────────────────────────────────
//
// The officer's own supplied identifiers (the case subject's UPI, phone,
// Telegram/Instagram handle, wallet, and their bank account) are tied to ONE
// hub node so "my data" forms a connected star instead of floating islands.
// Officer-asserted, so the edges are SCORED (dashed, court-safe-dimmed) — the
// officer grouped these; it isn't an on-chain proof.

export const SUBJECT_HUB_ID = 'ent:subject:case';
const DEFAULT_SUBJECT_LABEL = 'Subject (your data)';

/** Label to show on the subject hub for a given suspect name (blank ⇒ default). */
export const subjectHubLabel = (name?: string): string => {
  const n = (name || '').trim();
  return n ? n : DEFAULT_SUBJECT_LABEL;
};

/** Create the subject hub once (idempotent). Returns its id. Optionally names it. */
export function ensureSubjectHub(cy: cytoscape.Core, name?: string): string {
  if (cy.getElementById(SUBJECT_HUB_ID).empty()) {
    cy.add({ data: {
      id: SUBJECT_HUB_ID, label: subjectHubLabel(name), module: 'shared',
      type: 'identity_hub', risk_score: 0.5, size: 9, step: 1, placement_role: 'subject',
      subject_name: (name || '').trim() || undefined,
    } });
  } else if (name && name.trim()) {
    setSubjectName(cy, name);
  }
  return SUBJECT_HUB_ID;
}

/** Set (or clear) the case subject's name — updates the hub label live. Creates
 *  the hub if it doesn't exist yet so naming the subject always works. */
export function setSubjectName(cy: cytoscape.Core, name: string): void {
  ensureSubjectHub(cy);
  const hub = cy.getElementById(SUBJECT_HUB_ID);
  if (hub.empty()) return;
  const n = (name || '').trim();
  hub.data('label', subjectHubLabel(n));
  hub.data('subject_name', n || undefined);
}

// What each identifier IS to the subject — drives the edge label so a phone edge
// reads "phone number", a UPI edge "UPI ID", etc. (instead of every edge saying
// the same generic "subject identifier").
const SUBJECT_EDGE_LABEL: Record<string, string> = {
  phone: 'phone number',
  vpa: 'UPI ID',
  handle: 'social handle',
  account: 'bank account',
  wallet: 'wallet',
  email: 'email',
  device: 'device',
};

/** Describe the subject→identifier edge from the target node's type (falls back
 *  to the canonical `ent:<type>:` prefix, then a generic label). */
export function subjectEdgeLabel(cy: cytoscape.Core, nodeId: string): string {
  const n = cy.getElementById(nodeId);
  const type = n && n.nonempty() ? String(n.data('type') || '') : '';
  if (SUBJECT_EDGE_LABEL[type]) return SUBJECT_EDGE_LABEL[type];
  const m = /^ent:([a-z]+):/.exec(nodeId);
  if (m && SUBJECT_EDGE_LABEL[m[1]]) return SUBJECT_EDGE_LABEL[m[1]];
  return 'subject identifier';
}

/** Ingestion stage of a node (defaults to stage 1). Used so a join edge never
 *  reveals before the node it points at. */
const nodeStep = (cy: cytoscape.Core, id: string): number => {
  const n = cy.getElementById(id);
  return n.nonempty() ? Math.max(1, Number(n.data('step')) || 1) : 1;
};

/** Tie a user-entered identifier to the subject hub. Idempotent; no-op for the
 *  hub itself. Edge is scored (officer assertion), rendered dashed. Refreshes the
 *  label on an existing edge so re-runs pick up better attribution. The edge
 *  inherits the target's ingestion stage so it reveals with that node on replay. */
export function linkToSubject(cy: cytoscape.Core, nodeId: string): void {
  if (!nodeId || nodeId === SUBJECT_HUB_ID) return;
  ensureSubjectHub(cy);
  const eid = `subj:${nodeId}`;
  const label = subjectEdgeLabel(cy, nodeId);
  const existing = cy.getElementById(eid);
  if (existing.nonempty()) { existing.data('_edgeLabel', label); return; }
  cy.add({ data: {
    id: eid, source: SUBJECT_HUB_ID, target: nodeId,
    edge_type: 'cross_link', determinism: 'scored', step: nodeStep(cy, nodeId),
    _edgeLabel: label,
  } });
}

/**
 * Draw scored `shared identifier` edges between phone-keyed VPAs and their
 * phone nodes. Idempotent (stable edge ids); returns the ids it added.
 */
export function linkDerivedIdentifiers(cy: cytoscape.Core): string[] {
  const added: string[] = [];
  cy.nodes('[type = "vpa"]').forEach((v) => {
    const label = String(v.data('label') || '');
    const local = (label || v.id().replace(/^ent:vpa:/, '')).split('@')[0];
    const digits = local.replace(/\D/g, '');
    if (digits.length !== 10 || digits !== local) return;   // only pure phone-keyed VPAs
    const phone = cy.getElementById(`ent:phone:${digits}`);
    if (phone.empty()) return;
    const eid = `join:${phone.id()}~${v.id()}`;
    if (cy.getElementById(eid).nonempty()) return;
    cy.add({ data: {
      id: eid, source: phone.id(), target: v.id(),
      edge_type: 'cross_link', determinism: 'scored',
      step: Math.max(nodeStep(cy, phone.id()), nodeStep(cy, v.id())),
      _edgeLabel: 'shared identifier',
    } });
    added.push(eid);
  });
  return added;
}
